document.addEventListener('DOMContentLoaded', () => {
  /*
   * ==========================================================
   * BIOTHERAPEUTICS · QUICK ADD / CART DRAWER
   * MULTIDIOMA ES · EN · DE
   * ==========================================================
   *
   * Gestiona:
   *
   * 1. Botón antiguo del predictive search:
   *    .predictive-search__add-to-cart-btn
   *
   * 2. Botones del BT Search:
   *    [data-bt-add]
   *
   * 3. Botones "Añadir" del desplegable predictivo global.
   *
   * Respeta:
   *
   * - idioma activo de Shopify;
   * - rutas localizadas /en/ y /de/;
   * - drawer nativo de Impact;
   * - actualización de contador, totales y recomendaciones.
   * ==========================================================
   */


  const predictiveVariantCache =
    new Map();

  const predictiveVariantInflight =
    new Map();


  /* ==========================================================
     IDIOMAS
     ========================================================== */

  const TRANSLATIONS = {
    es: {
      add:
        'Añadir',

      adding:
        'Añadiendo…',

      addingOld:
        'Añadiendo...',

      added:
        'Añadido ✓',

      addedOld:
        '¡Añadido!',

      error:
        'Error',

      errorAdding:
        'Error al añadir',

      addToCart:
        product =>
          `Añadir ${product} al carrito`,

      loadProductError:
        product =>
          `No se pudo cargar el producto ${product}.`,

      cartAddError:
        'No se pudo añadir el producto al carrito.',

      cartRefreshError:
        'El producto se añadió, pero no se pudo actualizar el carrito.'
    },


    en: {
      add:
        'Add',

      adding:
        'Adding…',

      addingOld:
        'Adding...',

      added:
        'Added ✓',

      addedOld:
        'Added!',

      error:
        'Error',

      errorAdding:
        'Could not add',

      addToCart:
        product =>
          `Add ${product} to cart`,

      loadProductError:
        product =>
          `Could not load product ${product}.`,

      cartAddError:
        'The product could not be added to the cart.',

      cartRefreshError:
        'The product was added, but the cart could not be refreshed.'
    },


    de: {
      add:
        'Hinzufügen',

      adding:
        'Wird hinzugefügt…',

      addingOld:
        'Wird hinzugefügt...',

      added:
        'Hinzugefügt ✓',

      addedOld:
        'Hinzugefügt!',

      error:
        'Fehler',

      errorAdding:
        'Fehler beim Hinzufügen',

      addToCart:
        product =>
          `${product} in den Warenkorb legen`,

      loadProductError:
        product =>
          `Produkt ${product} konnte nicht geladen werden.`,

      cartAddError:
        'Das Produkt konnte nicht in den Warenkorb gelegt werden.',

      cartRefreshError:
        'Das Produkt wurde hinzugefügt, aber der Warenkorb konnte nicht aktualisiert werden.'
    }
  };


  function normalizeLocale(
    value
  ) {
    const locale =
      String(
        value || ''
      )
        .trim()
        .toLowerCase()
        .replace(
          '_',
          '-'
        )
        .split('-')[0];


    if (
      locale === 'de' ||
      locale === 'en' ||
      locale === 'es'
    ) {
      return locale;
    }


    return 'es';
  }


  function getLocale(
    source = null
  ) {
    /*
     * 1. Locale del propio BT Search.
     */

    if (
      source instanceof Element
    ) {
      const searchApp =
        source.closest(
          '[data-bt-search-app]'
        );


      const appLocale =
        searchApp?.dataset
          ?.locale;


      if (appLocale) {
        return normalizeLocale(
          appLocale
        );
      }
    }


    /*
     * 2. Shopify global.
     */

    if (
      window.Shopify?.locale
    ) {
      return normalizeLocale(
        window.Shopify.locale
      );
    }


    /*
     * 3. <html lang="...">
     */

    if (
      document.documentElement.lang
    ) {
      return normalizeLocale(
        document.documentElement.lang
      );
    }


    return 'es';
  }


  function translate(
    key,
    source = null
  ) {
    const locale =
      getLocale(
        source
      );


    return (
      TRANSLATIONS[locale]?.[key] ??
      TRANSLATIONS.es[key]
    );
  }


  /* ==========================================================
     RUTAS SHOPIFY
     ========================================================== */

  function getShopifyRoot() {
    const rawRoot =
      window.Shopify
        ?.routes
        ?.root ||
      '/';


    /*
     * Shopify.routes.root es sensible al idioma:
     *
     * ES → /
     * EN → /en/
     * DE → /de/
     *
     * Garantizamos siempre la barra final.
     */

    return rawRoot.endsWith('/')
      ? rawRoot
      : `${rawRoot}/`;
  }


  /* ==========================================================
     HELPERS
     ========================================================== */

  function getVariantId(
    button
  ) {
    return (
      button.getAttribute(
        'data-bt-add'
      ) ||
      button.getAttribute(
        'data-variant-id'
      ) ||
      ''
    );
  }


  function isBtSearchButton(
    button
  ) {
    return button.hasAttribute(
      'data-bt-add'
    );
  }


  function isBtSearchMobileButton(
    button
  ) {
    return (
      button.classList.contains(
        'btsearch-card__quick-add-mobile'
      ) ||
      button.classList.contains(
        'btsearch-predictive__add--mobile'
      )
    );
  }


  /* ==========================================================
     ICONOS
     ========================================================== */

  function loadingIcon() {
    return `
      <svg
        class="bt-search-cart-loading-icon"
        viewBox="0 0 24 24"
        width="19"
        height="19"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke-opacity=".25"
        ></circle>

        <path
          d="M21 12a9 9 0 0 0-9-9"
        ></path>
      </svg>
    `;
  }


  function successIcon() {
    return `
      <svg
        viewBox="0 0 24 24"
        width="19"
        height="19"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m5 12 4 4L19 6"></path>
      </svg>
    `;
  }


  function predictiveCartIcon() {
    return `
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle
          cx="9"
          cy="20"
          r="1"
        ></circle>

        <circle
          cx="18"
          cy="20"
          r="1"
        ></circle>

        <path
          d="M3 4h2l2.1 10.1a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 2-1.6L20 8H7"
        ></path>
      </svg>
    `;
  }


  function errorIcon() {
    return `
      <svg
        viewBox="0 0 24 24"
        width="19"
        height="19"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
        ></circle>

        <path
          d="M12 8v5"
        ></path>

        <path
          d="M12 17h.01"
        ></path>
      </svg>
    `;
  }


  /* ==========================================================
     BOTÓN PREDICTIVO ANTIGUO
     ========================================================== */

  function oldPredictiveLoadingHtml(
    button
  ) {
    return `
      <svg
        class="cart-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle
          cx="9"
          cy="21"
          r="1"
        ></circle>

        <circle
          cx="20"
          cy="21"
          r="1"
        ></circle>

        <path
          d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"
        ></path>
      </svg>

      <span class="btn-text">
        ${translate(
          'addingOld',
          button
        )}
      </span>
    `;
  }


  function oldPredictiveSuccessHtml(
    button
  ) {
    return `
      <svg
        class="cart-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline
          points="20 6 9 17 4 12"
        ></polyline>
      </svg>

      <span class="btn-text">
        ${translate(
          'addedOld',
          button
        )}
      </span>
    `;
  }


  /* ==========================================================
     ESTADOS DEL BOTÓN
     ========================================================== */

  function setButtonState(
    button,
    state,
    originalContent
  ) {
    const btSearch =
      isBtSearchButton(
        button
      );

    const mobile =
      isBtSearchMobileButton(
        button
      );


    if (
      state ===
      'loading'
    ) {
      button.disabled =
        true;

      button.setAttribute(
        'aria-busy',
        'true'
      );

      button.classList.add(
        'is-loading'
      );


      if (btSearch) {
        button.innerHTML =
          mobile
            ? loadingIcon()
            : translate(
                'adding',
                button
              );

      } else {
        button.innerHTML =
          oldPredictiveLoadingHtml(
            button
          );
      }


      return;
    }


    if (
      state ===
      'success'
    ) {
      button.classList.remove(
        'is-loading'
      );

      button.classList.add(
        'is-success'
      );


      if (btSearch) {
        button.innerHTML =
          mobile
            ? successIcon()
            : translate(
                'added',
                button
              );

      } else {
        button.innerHTML =
          oldPredictiveSuccessHtml(
            button
          );
      }


      return;
    }


    if (
      state ===
      'error'
    ) {
      button.classList.remove(
        'is-loading'
      );

      button.classList.add(
        'is-error'
      );


      if (btSearch) {
        button.innerHTML =
          mobile
            ? errorIcon()
            : translate(
                'errorAdding',
                button
              );

      } else {
        button.innerHTML = `
          ${errorIcon()}

          <span class="btn-text">
            ${translate(
              'error',
              button
            )}
          </span>
        `;
      }


      return;
    }


    button.classList.remove(
      'is-loading',
      'is-success',
      'is-error'
    );

    button.disabled =
      false;

    button.removeAttribute(
      'aria-busy'
    );

    button.innerHTML =
      originalContent;
  }


  /* ==========================================================
     POSTHOG
     ========================================================== */

  function trackBtSearchAdd(
    button,
    variantId
  ) {
    if (
      !isBtSearchButton(
        button
      )
    ) {
      return;
    }


    try {
      if (
        !window.posthog ||
        typeof window
          .posthog
          .capture !==
          'function'
      ) {
        return;
      }


      const searchApp =
        button.closest(
          '[data-bt-search-app]'
        );


      const input =
        searchApp
          ?.querySelector(
            '[data-bt-search-input]'
          );


      const isPredictive =
        Boolean(
          button.closest(
            '[data-bt-search-predictive]'
          )
        );


      const productRow =
        button.closest(
          '.btsearch-predictive__product-row, .btsearch-card'
        );


      const productTitle =
        productRow
          ?.querySelector(
            '.btsearch-predictive__title, .btsearch-card__title'
          )
          ?.textContent
          ?.trim()
        ||
        '';


      const vendor =
        productRow
          ?.querySelector(
            '.btsearch-predictive__vendor, .btsearch-card__vendor'
          )
          ?.textContent
          ?.trim()
        ||
        '';


      window.posthog.capture(
        'bt_search_add_to_cart',
        {
          query:
            input
              ?.value
              ?.trim()
            ||
            '',

          product_handle:
            button.getAttribute(
              'data-bt-handle'
            )
            ||
            '',

          product_title:
            productTitle,

          vendor:
            vendor,

          variant_id:
            Number(
              variantId
            ),

          position:
            Number(
              button.getAttribute(
                'data-bt-position'
              )
              ||
              0
            ),

          surface:
            isPredictive
              ? 'predictive'
              : 'results_page',

          locale:
            getLocale(
              button
            )
        }
      );

    } catch (_) {}
  }


  /* ==========================================================
     PRODUCTOS DEL DESPLEGABLE PREDICTIVO
     ========================================================== */

  function predictiveProductUrl(
    handle
  ) {
    const root =
      getShopifyRoot();


    return (
      `${root}products/${encodeURIComponent(
        handle
      )}.js`
    );
  }


  async function getPredictiveVariant(
    handle,
    source = null
  ) {
    if (!handle) {
      return null;
    }


    if (
      predictiveVariantCache.has(
        handle
      )
    ) {
      return predictiveVariantCache.get(
        handle
      );
    }


    if (
      predictiveVariantInflight.has(
        handle
      )
    ) {
      return predictiveVariantInflight.get(
        handle
      );
    }


    const request =
      fetch(
        predictiveProductUrl(
          handle
        ),
        {
          method:
            'GET',

          headers: {
            'Accept':
              'application/json',

            'X-Requested-With':
              'XMLHttpRequest'
          },

          credentials:
            'same-origin'
        }
      )
        .then(
          async response => {
            if (
              !response.ok
            ) {
              const message =
                translate(
                  'loadProductError',
                  source
                );


              throw new Error(
                typeof message ===
                  'function'
                  ? message(
                      handle
                    )
                  : `Could not load ${handle}`
              );
            }


            const product =
              await response.json();


            const variants =
              Array.isArray(
                product
                  ?.variants
              )
                ? product.variants
                : [];


            /*
             * Elegimos la primera variante realmente disponible.
             */

            const variant =
              variants.find(
                item =>
                  Boolean(
                    item
                      ?.available
                  )
              )
              ||
              null;


            const result =
              variant?.id
                ? {
                    id:
                      String(
                        variant.id
                      ),

                    available:
                      true
                  }
                : null;


            predictiveVariantCache.set(
              handle,
              result
            );


            return result;
          }
        )
        .catch(
          error => {
            console.warn(
              '[BT Search predictive cart]',
              error
            );


            predictiveVariantCache.set(
              handle,
              null
            );


            return null;
          }
        )
        .finally(
          () => {
            predictiveVariantInflight.delete(
              handle
            );
          }
        );


    predictiveVariantInflight.set(
      handle,
      request
    );


    return request;
  }


  function createPredictiveAddButton({
    variantId,
    handle,
    position,
    productTitle,
    source,
    mobile = false
  }) {
    const button =
      document.createElement(
        'button'
      );


    button.type =
      'button';


    button.setAttribute(
      'data-bt-add',
      variantId
    );


    button.setAttribute(
      'data-bt-handle',
      handle
    );


    button.setAttribute(
      'data-bt-position',
      position
    );


    const readableProduct =
      String(
        productTitle ||
        handle.replace(
          /-/g,
          ' '
        )
      ).trim();


    const ariaTranslation =
      translate(
        'addToCart',
        source
      );


    button.setAttribute(
      'aria-label',
      typeof ariaTranslation ===
        'function'
        ? ariaTranslation(
            readableProduct
          )
        : readableProduct
    );


    if (mobile) {
      button.className =
        'btsearch-predictive__add btsearch-predictive__add--mobile btsearch-card__quick-add-mobile';


      button.innerHTML =
        predictiveCartIcon();

    } else {
      button.className =
        'btsearch-predictive__add btsearch-predictive__add--desktop';


      button.innerHTML = `
        ${predictiveCartIcon()}

        <span>
          ${translate(
            'add',
            source
          )}
        </span>
      `;
    }


    return button;
  }


  async function enhancePredictiveProduct(
    productLink
  ) {
    if (
      !(
        productLink
          instanceof
          Element
      )
      ||
      productLink.closest(
        '.btsearch-predictive__product-row'
      )
    ) {
      return;
    }


    const handle =
      productLink.getAttribute(
        'data-bt-result-click'
      )
      ||
      '';


    if (!handle) {
      return;
    }


    const parent =
      productLink.parentNode;


    if (!parent) {
      return;
    }


    const row =
      document.createElement(
        'div'
      );


    row.className =
      'btsearch-predictive__product-row';


    row.setAttribute(
      'data-bt-predictive-product-row',
      handle
    );


    parent.insertBefore(
      row,
      productLink
    );


    row.appendChild(
      productLink
    );


    /*
     * Si el motor ya marca el producto como agotado,
     * no hacemos una petición adicional.
     */

    if (
      productLink.querySelector(
        '.btsearch-predictive__soldout'
      )
    ) {
      return;
    }


    const variant =
      await getPredictiveVariant(
        handle,
        productLink
      );


    if (
      !variant
        ?.available
      ||
      !variant
        ?.id
      ||
      !row.isConnected
    ) {
      return;
    }


    /*
     * Evita botones duplicados.
     */

    if (
      row.querySelector(
        '[data-bt-predictive-add]'
      )
    ) {
      return;
    }


    const position =
      productLink.getAttribute(
        'data-bt-position'
      )
      ||
      '0';


    const productTitle =
      productLink
        .querySelector(
          '.btsearch-predictive__title'
        )
        ?.textContent
        ?.trim()
      ||
      handle.replace(
        /-/g,
        ' '
      );


    const desktopButton =
      createPredictiveAddButton({
        variantId:
          variant.id,

        handle,

        position,

        productTitle,

        source:
          productLink,

        mobile:
          false
      });


    const mobileButton =
      createPredictiveAddButton({
        variantId:
          variant.id,

        handle,

        position,

        productTitle,

        source:
          productLink,

        mobile:
          true
      });


    desktopButton.setAttribute(
      'data-bt-predictive-add',
      ''
    );


    mobileButton.setAttribute(
      'data-bt-predictive-add',
      ''
    );


    row.appendChild(
      desktopButton
    );


    row.appendChild(
      mobileButton
    );
  }


  function enhancePredictiveResults(
    root = document
  ) {
    if (
      root instanceof Element &&
      root.matches(
        '.btsearch-predictive__product'
      )
    ) {
      enhancePredictiveProduct(
        root
      );
    }


    if (
      !root.querySelectorAll
    ) {
      return;
    }


    root
      .querySelectorAll(
        '.btsearch-predictive__product'
      )
      .forEach(
        productLink => {
          enhancePredictiveProduct(
            productLink
          );
        }
      );
  }


  /* ==========================================================
     OBSERVADOR DEL PREDICTIVO
     ========================================================== */

  const predictiveObserver =
    new MutationObserver(
      mutations => {
        for (
          const mutation
          of mutations
        ) {
          for (
            const node
            of mutation.addedNodes
          ) {
            if (
              !(
                node
                  instanceof
                  Element
              )
            ) {
              continue;
            }


            enhancePredictiveResults(
              node
            );
          }
        }
      }
    );


  predictiveObserver.observe(
    document.documentElement,
    {
      childList:
        true,

      subtree:
        true
    }
  );


  enhancePredictiveResults(
    document
  );


  /* ==========================================================
     AÑADIR AL CARRITO
     ========================================================== */

  async function addToCart(
    button
  ) {
    const variantId =
      getVariantId(
        button
      );


    if (
      !variantId ||
      button.disabled
    ) {
      return;
    }


    const originalContent =
      button.innerHTML;


    setButtonState(
      button,
      'loading',
      originalContent
    );


    /*
     * ========================================================
     * 1. PEDIR A IMPACT LAS SECCIONES QUE NECESITA
     * ========================================================
     *
     * CartDrawer escucha este evento y añade cart-drawer
     * automáticamente a sectionsToBundle.
     * ========================================================
     */

    const sectionsToBundle = [
      'variant-added'
    ];


    document.documentElement.dispatchEvent(
      new CustomEvent(
        'cart:prepare-bundled-sections',
        {
          bubbles:
            true,

          detail: {
            sections:
              sectionsToBundle
          }
        }
      )
    );


    const root =
      getShopifyRoot();


    const formData =
      new FormData();


    formData.set(
      'id',
      variantId
    );


    formData.set(
      'quantity',
      '1'
    );


    formData.set(
      'sections',
      sectionsToBundle.join(
        ','
      )
    );


    /*
     * Utilizamos también la ruta localizada.
     */

    formData.set(
      'sections_url',
      `${root}variants/${variantId}`
    );


    try {

      /*
       * ======================================================
       * 2. AÑADIR PRODUCTO
       * ======================================================
       */

      const response =
        await fetch(
          `${root}cart/add.js`,
          {
            method:
              'POST',

            headers: {
              'X-Requested-With':
                'XMLHttpRequest'
            },

            body:
              formData
          }
        );


      const responseJson =
        await response
          .json()
          .catch(
            () => null
          );


      if (
        !response.ok
      ) {
        throw new Error(
          responseJson
            ?.description
          ||
          responseJson
            ?.message
          ||
          translate(
            'cartAddError',
            button
          )
        );
      }


      /*
       * Si Impact utiliza página de carrito, respetamos
       * su comportamiento nativo.
       */

      if (
        window
          .themeVariables
          ?.settings
          ?.cartType ===
          'page'
        ||
        window
          .themeVariables
          ?.settings
          ?.pageType ===
          'cart'
      ) {
        /*
         * Registramos antes de abandonar la página.
         */

        trackBtSearchAdd(
          button,
          variantId
        );


        window.location.href =
          `${root}cart`;


        return;
      }


      /*
       * ======================================================
       * 3. ESTADO ACTUAL DEL CARRITO
       * ======================================================
       */

      const cartResponse =
        await fetch(
          `${root}cart.js`,
          {
            headers: {
              'X-Requested-With':
                'XMLHttpRequest'
            }
          }
        );


      if (
        !cartResponse.ok
      ) {
        throw new Error(
          translate(
            'cartRefreshError',
            button
          )
        );
      }


      const cartContent =
        await cartResponse.json();


      /*
       * Las secciones renderizadas llegan en /cart/add.js.
       */

      cartContent.sections =
        responseJson
          ?.sections
        ||
        {};


      /*
       * Cerramos el predictive antes de abrir el carrito.
       */

      document
        .querySelectorAll(
          '[data-bt-search-predictive]'
        )
        .forEach(
          predictive => {
            predictive.hidden =
              true;
          }
        );


      /*
       * ======================================================
       * 4. ABRIR DRAWER
       * ======================================================
       */

      document.dispatchEvent(
        new CustomEvent(
          'variant:add',
          {
            bubbles:
              true,

            detail: {
              items:
                responseJson &&
                Object.prototype
                  .hasOwnProperty
                  .call(
                    responseJson,
                    'items'
                  )
                  ? responseJson.items
                  : [
                      responseJson
                    ],

              cart:
                cartContent
            }
          }
        )
      );


      /*
       * ======================================================
       * 5. ACTUALIZAR DRAWER + CONTADOR + TOTAL
       * ======================================================
       */

      document.documentElement.dispatchEvent(
        new CustomEvent(
          'cart:change',
          {
            bubbles:
              true,

            detail: {
              baseEvent:
                'variant:add',

              cart:
                cartContent
            }
          }
        )
      );


      setButtonState(
        button,
        'success',
        originalContent
      );


      /*
       * Solo registramos el evento tras haber confirmado
       * que Shopify aceptó el producto.
       */

      trackBtSearchAdd(
        button,
        variantId
      );


      setTimeout(
        () => {
          setButtonState(
            button,
            'idle',
            originalContent
          );
        },
        1800
      );

    } catch (error) {

      console.error(
        '[BioTherapeutics cart]',
        error
      );


      setButtonState(
        button,
        'error',
        originalContent
      );


      /*
       * Fallback de Impact.
       */

      document.dispatchEvent(
        new CustomEvent(
          'cart:refresh',
          {
            bubbles:
              true
          }
        )
      );


      setTimeout(
        () => {
          setButtonState(
            button,
            'idle',
            originalContent
          );
        },
        2000
      );
    }
  }


  /* ==========================================================
     CLICK HANDLER EN CAPTURE
     ==========================================================
   *
   * BTSearchApp también escucha [data-bt-add].
   *
   * Usamos capture:true para evitar dos peticiones al carrito.
   * ========================================================== */

  document.addEventListener(
    'click',
    event => {
      if (
        !(
          event.target
            instanceof
            Element
        )
      ) {
        return;
      }


      const button =
        event.target.closest(
          '.predictive-search__add-to-cart-btn, [data-bt-add]'
        );


      if (!button) {
        return;
      }


      /*
       * Dejamos referencia del elemento para cualquier
       * integración analítica del motor.
       */

      window
        .__BT_SEARCH_LAST_INTERACTION_TARGET__ =
        button;


      event.preventDefault();

      event.stopPropagation();

      event.stopImmediatePropagation();


      addToCart(
        button
      );
    },
    true
  );


  /* ==========================================================
     SPINNER
     ========================================================== */

  if (
    !document.getElementById(
      'bt-search-cart-integration-style'
    )
  ) {
    const style =
      document.createElement(
        'style'
      );


    style.id =
      'bt-search-cart-integration-style';


    style.textContent = `
      @keyframes bt-search-cart-spin {
        from {
          transform: rotate(0deg);
        }

        to {
          transform: rotate(360deg);
        }
      }

      .bt-search-cart-loading-icon,
      .predictive-search__add-to-cart-btn.is-loading .cart-icon {
        animation:
          bt-search-cart-spin
          .8s
          linear
          infinite;
      }
    `;


    document.head.appendChild(
      style
    );
  }

});